import { Button, Empty } from 'antd';
import merge from 'deepmerge';

import { LuckyWheelHistoryQuery, useLuckyWheelHistoryQuery } from '@hooks/luckywheel/luckyWheel.gql.generated';
import { toDDMMYYYY } from '@utils/datetime';
import { notNullish } from '@utils/typeUtils';

import s from './LuckyWheelHistory.module.less';
import { LoadingContainer } from '@sharedComponents';
import { truncateText } from '@utils/formatter';
import { PERFORMANCE_ITEM_PER_PAGE } from '@utils/constants';
import { useTranslation } from 'next-i18next';

interface ILuckyWheelHistoryProps {
  campaignId: string;
}

export default function LuckyWheelHistory(props: ILuckyWheelHistoryProps) {
  const { t: tLayout } = useTranslation('layout');

  const { campaignId } = props;

  const {
    data: historyData,
    loading,
    fetchMore,
  } = useLuckyWheelHistoryQuery({
    variables: {
      campaignId,
      first: PERFORMANCE_ITEM_PER_PAGE,
    },
  });

  const history = (historyData?.luckyWheelHistory.nodes || []).filter(notNullish);
  const hasNextPage = historyData?.luckyWheelHistory.pageInfo.hasNextPage;
  const endCursor = historyData?.luckyWheelHistory.pageInfo.endCursor;

  function handleFetchMoreHistory() {
    if (!endCursor) return;

    fetchMore({
      variables: {
        after: endCursor,
      },
      updateQuery: (prev: LuckyWheelHistoryQuery, { fetchMoreResult }): LuckyWheelHistoryQuery => {
        const moreHistory = fetchMoreResult?.luckyWheelHistory?.nodes || [];

        if (!moreHistory.length) return prev;

        return {
          ...prev,
          luckyWheelHistory: {
            ...merge(prev.luckyWheelHistory, fetchMoreResult.luckyWheelHistory),
          },
        };
      },
    });
  }

  if (!historyData && loading) return <LoadingContainer />;

  if (!historyData) return <Empty description={tLayout('spin.empty_history')} />;

  return (
    <div className={s.root}>
      <div className={s.heading}>
        <p>{tLayout('spin.turn_times')}</p>
        <p>{tLayout('spin.present')}</p>
        <p>{tLayout('spin.cameraman')}</p>
        <p>{tLayout('spin.time')}</p>
      </div>
      <div className={s.content}>
        {history.map(record => (
          <div key={record.id} className={s.item}>
            <p>
              {tLayout('spin.time_1')} {record.turn}
            </p>
            <p>{truncateText(record.name, 40)}</p>
            <p>{record.user.name}</p>
            <p>{toDDMMYYYY(record.createdAt)}</p>
          </div>
        ))}
      </div>
      {hasNextPage && (
        <div className={s.buttons}>
          <Button type="primary" onClick={handleFetchMoreHistory} loading={loading}>
            {tLayout('search_bar.load_more')}
          </Button>
        </div>
      )}
    </div>
  );
}
